import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CELLS } from "@/constants/testIds";
import { toast, confirmToast } from "@/lib/notify";
import PrintPreviewButton from "@/components/PrintPreviewButton";
import { Pencil, Trash2, X, Save, User, Users, MapPin, CalendarDays, StickyNote } from "lucide-react";

const MONTHS_FULL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fmtMoney = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_CAPTURE = () => ({
  date: today(),
  adults: "",
  attendance_kids: "",
  guests: "",
  converts: "",
  reconciled: "",
  offering: "",
  leader_notes: "",
});

const numInputCls =
  "w-full border border-[#d9cfb8] bg-white px-2 py-2 text-[13px] text-center rounded focus:border-[#c8a04a] focus:outline-none tabular-nums";
const inputCls =
  "w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none";

const NUM_FIELDS = [
  { key: "adults", label: "Adultos" },
  { key: "attendance_kids", label: "Niños" },
  { key: "guests", label: "Invitados" },
  { key: "converts", label: "Convertidos" },
  { key: "reconciled", label: "Reconciliados" },
];

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-[13px]">
      <Icon size={14} className="mt-0.5 shrink-0 text-[#7a5b0f]" />
      <div>
        <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e] block leading-tight">{label}</span>
        <span className="text-[#1c1a14]">{value || "—"}</span>
      </div>
    </div>
  );
}

export default function CellRecord({ cell, onEdit, onDelete, onClose, onDataChanged }) {
  const [captures, setCaptures] = useState([]);
  const [capYear, setCapYear] = useState(new Date().getFullYear());
  const [capMonth, setCapMonth] = useState(0); // 0 = todo el año
  const [form, setForm] = useState(EMPTY_CAPTURE());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const visibleCaptures = capMonth
    ? captures.filter((c) => Number((c.date || "").slice(5, 7)) === capMonth)
    : captures;

  const totals = visibleCaptures.reduce(
    (t, c) => ({
      adults: t.adults + (c.adults || 0),
      kids: t.kids + (c.attendance_kids || 0),
      guests: t.guests + (c.guests || 0),
      converts: t.converts + (c.converts || 0),
      reconciled: t.reconciled + (c.reconciled || 0),
      offering: t.offering + (c.offering || 0),
    }),
    { adults: 0, kids: 0, guests: 0, converts: 0, reconciled: 0, offering: 0 },
  );

  const periodLabel = capMonth ? `${MONTHS_FULL[capMonth - 1]} ${capYear}` : `Año ${capYear}`;

  const yearOptions = [];
  for (let y = new Date().getFullYear() + 1; y >= 2022; y--) yearOptions.push(y);

  const loadCaptures = useCallback(async () => {
    try {
      const { data } = await api.get(`/cells/${cell.id}/captures`, { params: { year: capYear } });
      setCaptures([...(data || [])].sort((a, b) => (a.date < b.date ? 1 : -1)));
    } catch {
      toast.error("Error cargando capturas");
    }
  }, [cell.id, capYear]);

  useEffect(() => {
    loadCaptures();
    setForm(EMPTY_CAPTURE());
    setEditingId(null);
  }, [loadCaptures]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k, v) => set(k, v.replace(/[^0-9]/g, ""));

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      date: c.date,
      adults: String(c.adults || ""),
      attendance_kids: String(c.attendance_kids || ""),
      guests: String(c.guests || ""),
      converts: String(c.converts || ""),
      reconciled: String(c.reconciled || ""),
      offering: c.offering ? String(c.offering) : "",
      leader_notes: c.leader_notes || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_CAPTURE());
  };

  const saveCapture = async (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.error("La fecha es obligatoria");
      return;
    }
    const adults = Number(form.adults) || 0;
    const kids = Number(form.attendance_kids) || 0;
    const guests = Number(form.guests) || 0;
    const payload = {
      date: form.date,
      adults,
      attendance_kids: kids,
      guests,
      converts: Number(form.converts) || 0,
      reconciled: Number(form.reconciled) || 0,
      offering: Number(form.offering) || 0,
      leader_notes: form.leader_notes || "",
      attendance_total: adults + kids + guests,
    };
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/cell-captures/${editingId}`, payload);
        toast.success("Captura modificada");
      } else {
        await api.post(`/cells/${cell.id}/captures`, payload);
        toast.success("Captura guardada");
      }
      cancelEdit();
      await loadCaptures();
      onDataChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar la captura");
    } finally {
      setBusy(false);
    }
  };

  const deleteCapture = async (c) => {
    const ok = await confirmToast(`¿Eliminar la captura del ${c.date}?`, { confirmLabel: "Eliminar" });
    if (!ok) return;
    try {
      await api.delete(`/cell-captures/${c.id}`);
      toast.success("Captura eliminada");
      if (editingId === c.id) cancelEdit();
      await loadCaptures();
      onDataChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div data-testid={CELLS.record} className="border border-[#d9cfb8] bg-white mb-6 max-w-2xl">
      {/* Cell info header */}
      <div className="flex items-start justify-between gap-3 border-b border-[#e8e1cd] bg-[#fbf7ee] px-5 py-4">
        <div>
          <h3 className="font-serif text-xl text-[#1c1a14]">{cell.name}</h3>
          <span
            className={`inline-block mt-1 rounded px-2 py-0.5 text-[9px] uppercase tracking-widest border ${
              cell.active
                ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                : "border-[#d9cfb8] bg-white text-[#8b7e5e]"
            }`}
          >
            {cell.active ? "Activa" : "Inactiva"}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <PrintPreviewButton testid={CELLS.printButton} />
          <button data-testid={CELLS.recordEdit} onClick={onEdit} title="Modificar célula"
            className="inline-flex items-center gap-1.5 border border-[#1c2a3a] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#1c2a3a] hover:bg-[#1c2a3a] hover:text-white transition">
            <Pencil size={12} /> Modificar
          </button>
          <button data-testid={CELLS.recordDelete} onClick={onDelete} title="Eliminar célula"
            className="inline-flex items-center gap-1.5 border border-red-300 px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700 hover:bg-red-50">
            <Trash2 size={12} /> Eliminar
          </button>
          <button data-testid={CELLS.recordClose} onClick={onClose} title="Cerrar registro"
            className="border border-[#d9cfb8] px-2 py-1.5 text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Vertical info */}
      <div className="px-5 py-4 space-y-3">
        <InfoRow icon={User} label="Nombre del líder" value={cell.leader_name} />
        <InfoRow icon={User} label="Sublíder" value={cell.subleader} />
        <InfoRow icon={Users} label="Hermanos de apoyo" value={cell.support} />
        <InfoRow icon={MapPin} label="Condado" value={cell.county} />
        <InfoRow icon={CalendarDays} label="Días / Horario" value={cell.schedule} />
        <InfoRow icon={MapPin} label="Dirección" value={cell.address} />
        <InfoRow icon={StickyNote} label="Notas" value={cell.notes} />
        <InfoRow icon={Users} label="Miembros asignados" value={String(cell.member_count)} />
      </div>

      {/* Capture form (vertical) */}
      <form data-testid={CELLS.capForm} onSubmit={saveCapture}
        className="border-t border-[#e8e1cd] px-5 py-4">
        <h4 className="text-[11px] uppercase tracking-[0.18em] text-[#7a5b0f] mb-3">
          {editingId ? "Modificar captura de asistencia" : "Nueva captura de asistencia"}
        </h4>
        <div className="max-w-xs mb-3">
          <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">Fecha *</span>
          <input data-testid={CELLS.capDate} type="date" value={form.date}
            onChange={(e) => set("date", e.target.value)} className={inputCls} required />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          {NUM_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="block text-[9px] uppercase tracking-widest text-[#8b7e5e] mb-1 text-center">
                {f.label}
              </span>
              <input data-testid={CELLS.capField(f.key)} inputMode="numeric" placeholder="0"
                value={form[f.key]} onChange={(e) => setNum(f.key, e.target.value)}
                className={numInputCls} />
            </label>
          ))}
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#8b7e5e] mb-1 text-center">
              Ofrenda $
            </span>
            <input data-testid={CELLS.capOffering} inputMode="decimal" placeholder="0.00"
              value={form.offering}
              onChange={(e) => set("offering", e.target.value.replace(/[^0-9.]/g, ""))}
              className={numInputCls} />
          </label>
        </div>
        <div className="mb-3">
          <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">Notas</span>
          <input data-testid={CELLS.capNotes} value={form.leader_notes}
            onChange={(e) => set("leader_notes", e.target.value)} className={inputCls}
            placeholder="Tema, peticiones, observaciones…" />
        </div>
        <div className="flex gap-2">
          <button data-testid={CELLS.capSave} type="submit" disabled={busy}
            className="inline-flex items-center gap-2 bg-[#c8a04a] hover:bg-[#b8902a] text-[#1c1a14] px-4 py-2 text-[11px] uppercase tracking-widest disabled:opacity-60">
            <Save size={13} /> {busy ? "Guardando..." : editingId ? "Actualizar captura" : "Guardar captura"}
          </button>
          {editingId && (
            <button data-testid={CELLS.capCancelEdit} type="button" onClick={cancelEdit}
              className="border border-[#d9cfb8] px-4 py-2 text-[11px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]">
              Cancelar
            </button>
          )}
        </div>
      </form>

      {/* Captures list */}
      <div className="border-t border-[#e8e1cd] px-5 py-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h4 className="text-[11px] uppercase tracking-[0.18em] text-[#7a5b0f]">
            Capturas registradas · {periodLabel}
          </h4>
          <div className="flex gap-2">
            <select data-testid={CELLS.capMonthSelect} value={capMonth}
              onChange={(e) => setCapMonth(Number(e.target.value))}
              className="border border-[#d9cfb8] bg-white px-2 py-1 text-[12px] rounded focus:border-[#c8a04a] focus:outline-none">
              <option value={0}>Todo el año</option>
              {MONTHS_FULL.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
            </select>
            <select data-testid={CELLS.capYearSelect} value={capYear}
              onChange={(e) => setCapYear(Number(e.target.value))}
              className="border border-[#d9cfb8] bg-white px-2 py-1 text-[12px] rounded focus:border-[#c8a04a] focus:outline-none">
              {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
        </div>
        {visibleCaptures.length === 0 ? (
          <div className="text-[12px] text-[#8b7e5e] border border-dashed border-[#d9cfb8] px-4 py-4 text-center">
            Sin capturas en {periodLabel.toLowerCase()}.
          </div>
        ) : (
          <>
          <div data-testid={CELLS.capList} className="divide-y divide-[#e8e1cd] border border-[#e8e1cd]">
            {visibleCaptures.map((c) => (
              <div key={c.id} data-testid={CELLS.capItem(c.id)}
                className={`px-3 py-2 text-[12px] hover:bg-[#fbf7ee] ${editingId === c.id ? "bg-[#fdf6e3]" : ""}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-semibold tabular-nums text-[#1c1a14]">{c.date}</span>
                  <span className="text-[#5d5849]">
                    Adultos <b>{c.adults}</b> · Niños <b>{c.attendance_kids}</b> · Invitados <b>{c.guests}</b> ·
                    Convertidos <b>{c.converts}</b> · Reconciliados <b>{c.reconciled}</b>
                  </span>
                  <span className="text-[#7a5b0f] font-semibold tabular-nums">{fmtMoney(c.offering)}</span>
                  <span className="ml-auto flex gap-2">
                    <button data-testid={CELLS.capEdit(c.id)} onClick={() => startEdit(c)}
                      title="Modificar" className="text-[#1c2a3a] hover:text-[#c8a04a]">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={CELLS.capDelete(c.id)} onClick={() => deleteCapture(c)}
                      title="Eliminar" className="text-red-700 hover:text-red-900">
                      <Trash2 size={13} />
                    </button>
                  </span>
                </div>
                {c.leader_notes && (
                  <div className="mt-1 text-[11px] text-[#8b7e5e]">{c.leader_notes}</div>
                )}
              </div>
            ))}
          </div>
          <div data-testid={CELLS.capTotals}
            className="mt-2 text-[12px] text-[#1c1a14] bg-[#fbf7ee] border border-[#e8e1cd] px-3 py-2">
            <b>Totales {periodLabel}:</b>{" "}
            Adultos <b>{totals.adults}</b> · Niños <b>{totals.kids}</b> · Invitados <b>{totals.guests}</b> ·
            Convertidos <b>{totals.converts}</b> · Reconciliados <b>{totals.reconciled}</b> ·
            Ofrenda <b className="text-[#7a5b0f]">{fmtMoney(totals.offering)}</b>
          </div>
          </>
        )}
      </div>

      {/* Hidden print layout: cell info + captures of the selected period */}
      <div className="print-area hidden">
        <h2 style={{ fontFamily: "serif", marginBottom: 2 }}>Célula: {cell.name}</h2>
        <p style={{ margin: "2px 0", fontSize: 13 }}>
          <b>Líder:</b> {cell.leader_name || "—"} &nbsp;·&nbsp; <b>Sublíder:</b> {cell.subleader || "—"}
          &nbsp;·&nbsp; <b>Hermanos de apoyo:</b> {cell.support || "—"}
        </p>
        <p style={{ margin: "2px 0", fontSize: 13 }}>
          <b>Condado:</b> {cell.county || "—"} &nbsp;·&nbsp; <b>Días/Horario:</b> {cell.schedule || "—"}
          &nbsp;·&nbsp; <b>Dirección:</b> {cell.address || "—"}
        </p>
        {cell.notes && (
          <p style={{ margin: "2px 0", fontSize: 13 }}><b>Notas:</b> {cell.notes}</p>
        )}
        <h3 style={{ margin: "12px 0 6px", fontSize: 14 }}>
          Capturas de asistencia — {periodLabel}
        </h3>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} border="1" cellPadding="4">
          <thead>
            <tr style={{ background: "#f0e9d8" }}>
              <th>Fecha</th><th>Adultos</th><th>Niños</th><th>Invitados</th>
              <th>Convertidos</th><th>Reconciliados</th><th>Ofrenda</th><th>Notas</th>
            </tr>
          </thead>
          <tbody>
            {visibleCaptures.map((c) => (
              <tr key={c.id}>
                <td>{c.date}</td>
                <td style={{ textAlign: "center" }}>{c.adults}</td>
                <td style={{ textAlign: "center" }}>{c.attendance_kids}</td>
                <td style={{ textAlign: "center" }}>{c.guests}</td>
                <td style={{ textAlign: "center" }}>{c.converts}</td>
                <td style={{ textAlign: "center" }}>{c.reconciled}</td>
                <td style={{ textAlign: "right" }}>{fmtMoney(c.offering)}</td>
                <td>{c.leader_notes || ""}</td>
              </tr>
            ))}
            <tr style={{ background: "#f0e9d8", fontWeight: "bold" }}>
              <td>Totales</td>
              <td style={{ textAlign: "center" }}>{totals.adults}</td>
              <td style={{ textAlign: "center" }}>{totals.kids}</td>
              <td style={{ textAlign: "center" }}>{totals.guests}</td>
              <td style={{ textAlign: "center" }}>{totals.converts}</td>
              <td style={{ textAlign: "center" }}>{totals.reconciled}</td>
              <td style={{ textAlign: "right" }}>{fmtMoney(totals.offering)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
